import { fetchMeanReversionData } from "../../../../Utilities/Url";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "@mui/material/styles";
import { Skeleton, Stack, TableFooter, TablePagination } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { utils, writeFile } from "xlsx";
import { canExportToCSV } from "../config";

const MeanReversion = () => {
  const dispatch = useDispatch();
  const selectedExchange = useSelector((state) => state.SetExchange);
  const SelectedDropdownOption = useSelector(
    (state) => state.SelectedDropdownOption
  );
  const authToken = useSelector((state) => state.authToken);
  const meanReversion = useSelector((state) => state.meanReversion) || [];
  const isLoading = useSelector((state) => state.isLoading);
  const rowsPerPage = useSelector((state) => state.RowsPerPage);
  const page = useSelector((state) => state.SetPage);
  const searchItem = useSelector((state) => state.SearchItems);

  useEffect(() => {
    if (SelectedDropdownOption === "Mean_Reversion") {
      fetchMeanReversionData(dispatch, selectedExchange, authToken);
    }
  }, [SelectedDropdownOption, selectedExchange, authToken, dispatch]);

  useEffect(() => {
    dispatch({ type: "SET_PAGE", payload: 0 });
  }, [searchItem, dispatch]);

  // Styled components
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: "center",
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#ebebeb",
      color: theme.palette.common.black,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  }));
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:last-child td, &:last-child th": {
      border: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
    },
  }));

  // Search Filtering
  const currentSearchTerm =
    searchItem.length > 0 ? searchItem[searchItem.length - 1] : "";

  const filterTicker = currentSearchTerm
    ? meanReversion.filter((ele) =>
        String(ele[1])?.toLowerCase().includes(currentSearchTerm.toLowerCase())
      )
    : meanReversion;

  // Pagination
  const rowsPerPageOptions = [5, 10, 25];
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedData = filterTicker?.slice(startIndex, endIndex);

  const handleChangePage = (event, newPage) => {
    dispatch({ type: "SET_PAGE", payload: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch({
      type: "ROWS_PER_PAGE",
      payload: parseInt(event.target.value, 10),
    });
  };

  // Pagination Buttons
  const TablePaginationActions = (props) => {
    const { count, page, rowsPerPage, onPageChange } = props;

    return (
      <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
        <IconButton
          onClick={(event) => onPageChange(event, 0)}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPageIcon />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page - 1)}
          disabled={page === 0}
          aria-label="previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={(event) => onPageChange(event, page + 1)}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          onClick={(event) =>
            onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
          }
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPageIcon />
        </IconButton>
      </div>
    );
  };

  // CSV Export Function
  const handleExportCSV = () => {
    const headers = [
      "Ticker",
      "Lower bound 1mm",
      "Mean 1mm",
      "Upper bound 1mm",
      "Lower bound 3mm",
      "Mean 3mm",
      "Upper bound 3mm",
    ];

    const csvData = displayedData.map((item) => [
      item[1] || "",
      item[6]?.toFixed(2) || "",
      item[4]?.toFixed(2) || "",
      item[5]?.toFixed(2) || "",
      item[9]?.toFixed(2) || "",
      item[7]?.toFixed(2) || "",
      item[8]?.toFixed(2) || "",
    ]);

    const data = [headers, ...csvData];
    const ws = utils.aoa_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "MeanReversion");

    writeFile(wb, "MeanReversion.xlsx");
  };

  return (
    <div>
      {isLoading ? (
        <Stack spacing={1} padding={"5px"}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={50}
            animation="wave"
          />
          <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
            <Skeleton
              variant="rectangular"
              width={300}
              height={100}
              animation="wave"
            />
          </div>
        </Stack>
      ) : (
        <TableContainer sx={{ width: "100%", position: "relative" }}>
          {canExportToCSV && (
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                padding: "6px",
                cursor: "pointer",
              }}
            >
              <DownloadIcon
                onClick={handleExportCSV}
                aria-label="export to CSV"
              />
            </div>
          )}
          <Table
            stickyHeader
            sx={{ tableLayout: "fixed", width: "100%" }}
            size="small"
          >
            <TableHead>
              <StyledTableRow>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  SYMBOLS
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Lower bound <br /> 1m
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Mean <br /> 1m
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Upper bound <br /> 1m
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Lower bound <br /> 3m
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Mean <br /> 3m
                </StyledTableCell>
                <StyledTableCell
                  sx={{ fontWeight: "bold", textAlign: "center" }}
                >
                  Upper bound <br /> 3m
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>

            <TableBody>
              {displayedData.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell sx={{ textAlign: "left" }}>
                    {row[1]}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {row[6].toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {row[4].toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {row[5].toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ textAlign: "right", backgroundColor: "#e0e9ff9c" }}
                  >
                    {row[9].toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ textAlign: "right", backgroundColor: "#e0e9ff9c" }}
                  >
                    {row[7].toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell
                    sx={{ textAlign: "right", backgroundColor: "#e0e9ff9c" }}
                  >
                    {row[8].toFixed(2)}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={filterTicker.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </TableContainer>
      )}
    </div>
  );
};

export default MeanReversion;
